import React, { useRef, useState } from "react";
import lang from "../../utils/languageConstants";
import { useDispatch, useSelector } from "react-redux";
import { textModel } from "../../utils/openai";
import { API_OPTIONS } from "../../utils/constants";
import { addGptMovieResults } from "../../store/slices/gptSlice";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Sparkles, Loader2, X, Command } from "lucide-react";
import { RootState } from "../../store";

const GptSearchBar = () => {
  const dispatch = useDispatch();
  const langKey = useSelector((store: RootState) => store.config.lang);
  const movieNames = useSelector((store: RootState) => store.gpt.movieNames);
  const searchText = useRef<HTMLInputElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const searchMovieTMDB = async (movie: string) => {
    try {
      const data = await fetch(
        "https://api.themoviedb.org/3/search/movie?query=" +
        encodeURIComponent(movie) +
        "&include_adult=false&language=en-US&page=1",
        API_OPTIONS
      );
      const json = await data.json();
      return json.results || [];
    } catch (error) {
      console.error("TMDB search error:", error);
      return [];
    }
  };

  const handleGptSearchClick = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const query = searchText.current?.value || inputValue;
    if (!query) return;

    setIsSearching(true);
    try {
      const prompt = `Act as a movie recommendation system. 
      Suggest 5 movies for the mood or query: "${query}".
      Return ONLY comma-separated movie names. 
      Example: Inception, The Dark Knight, Interstellar, Tenet, Memento`;

      const result = await textModel.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      if (!text) throw new Error("Empty response from AI");

      const gptMovies = text
        .split(",")
        .map((movie) => movie.trim().replace(/["*•]/g, "").replace(/\d+\./g, "").replace(/\.$/, ""))
        .filter(movie => movie.length > 0)
        .slice(0, 5);

      if (gptMovies.length === 0) throw new Error("No movies found in AI response");

      console.log("GPT Movies:", gptMovies);

      const tmdbResults = await Promise.all(
        gptMovies.map((movie) => searchMovieTMDB(movie))
      );

      console.log("TMDB Results:", tmdbResults);

      dispatch(
        addGptMovieResults({
          movieNames: gptMovies,
          movieResults: tmdbResults,
        })
      );
    } catch (error) {
      console.error("AI API Error:", error);
      alert("Failed to get recommendations. Please check your AI API key or try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleGenreClick = (genre: string) => {
    setInputValue(genre);
    // Use the value directly since ref might not have updated yet
    // But better to just update state and let user click search or trigger it
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-4xl text-center flex flex-col items-center"
      >
        <AnimatePresence mode="wait">
          {!movieNames && (
            <motion.div
              key="header"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30, scale: 0.98 }}
              transition={{ duration: 0.5 }}
              className="mb-10 flex flex-col items-center"
            >
              <div className="flex items-center space-x-2 bg-red-600/10 border border-red-600/20 px-4 py-1.5 rounded-full text-red-500 font-bold tracking-widest text-[9px] md:text-[10px] uppercase mb-6">
                <Sparkles size={12} className="animate-pulse" />
                <span>AI Discovery</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-none mb-6">
                Smart Movie <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-400">Discovery</span>
              </h1>
              <p className="text-zinc-400 text-base md:text-lg font-medium max-w-xl leading-relaxed text-center">
                {lang[langKey as keyof typeof lang]?.gptSearchPlaceholder || "Tell us what you're in the mood for, and our AI will find the perfect match."}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          layout
          className="relative w-full max-w-3xl"
        >
          <form
            onSubmit={handleGptSearchClick}
            className="group relative flex items-center bg-zinc-900/60 backdrop-blur-3xl border border-white/10 rounded-2xl p-1.5 shadow-2xl transition-all duration-500 focus-within:border-red-600/50 focus-within:ring-2 focus-within:ring-red-600/10"
          >
            <div className="flex-1 flex items-center pl-4 md:pl-6">
              <Search className="text-zinc-500 group-focus-within:text-red-500 transition-colors" size={20} />
              <input
                ref={searchText}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full bg-transparent border-none focus:ring-0 text-white px-4 py-3.5 text-base md:text-lg placeholder:text-zinc-700 font-semibold"
                placeholder="Search for themes, moods, or genres..."
                disabled={isSearching}
              />
              <AnimatePresence>
                {inputValue && (
                  <motion.button
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    type="button"
                    onClick={() => setInputValue("")}
                    className="p-2 mr-2 hover:bg-white/10 rounded-full text-zinc-400 transition-colors"
                  >
                    <X size={18} />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            <button
              type="submit"
              disabled={isSearching}
              className="relative flex items-center justify-center space-x-2 bg-red-600 text-white px-6 md:px-10 py-3 md:py-4 rounded-xl font-bold text-sm md:text-base transition-all duration-300 hover:bg-red-700 active:scale-95 disabled:bg-zinc-800 disabled:text-zinc-600"
            >
              {isSearching ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span className="hidden md:inline">Searching...</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>{lang[langKey as keyof typeof lang]?.search || "Search"}</span>
                </>
              )}
            </button>
          </form>

          {!movieNames && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col items-center space-y-4 mt-12"
            >
              <span className="text-zinc-500 text-[9px] font-black uppercase tracking-[0.2em]">Try these</span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {["Space Adventures", "90s Sci-Fi", "Mind-bending", "Feel-good", "Indie"].map((genre) => (
                  <button
                    key={genre}
                    onClick={() => handleGenreClick(genre)}
                    className="px-4 py-2 bg-zinc-900/40 border border-white/5 rounded-full text-[10px] md:text-xs text-zinc-400 hover:bg-zinc-800 hover:text-white hover:border-zinc-700 transition-all duration-300 flex items-center space-x-2 group/btn"
                  >
                    <Command size={12} className="text-zinc-600 group-hover/btn:text-red-500 transition-colors" />
                    <span>{genre}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default GptSearchBar;
